import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, User } from 'lucide-react';
import type { SessionLite } from '../../../../../types/session-lite';
import { SessionRow } from '../../sessions/components/SessionRow';
import { EmptyState } from '../../../../../components/common/EmptyState';
import { SESSION_APPOINTMENT_TYPES } from '../../../../../hooks/api/config';
import { parseSessionDate, toISODate } from '../../../../../utils/sessions';
import { useWidth } from '../../../../../hooks/useWidth';
import { timezoneToCountryCode } from '../../../../../utils/timezoneToCountry';
import CountryFlag from '../../../../../components/common/CountryFlag';
import { isoToZoneParts, localTzToUTC } from '../../../../../components/psychologist/calendar/timeZoneHelpers';
import { usePsychologistContext } from '../../../../../contexts/PsychologistContext';
import { BA_TIMEZONE } from '../../../../../utils/timezone';
// Mobile: 6rem/h keeps compact blocks proportional to duration
const ROW_HEIGHT_REM = 6;
// Desktop: 5rem/h (80px) — tighter cards, consistent ~16px gap below each card
const DESKTOP_ROW_HEIGHT_REM = 5;
// Gap in px between consecutive desktop card wrappers
const CARD_GAP_PX = 2;
// Gap subtracted from each mobile block so consecutive sessions have a visible white separator
const MOBILE_BLOCK_GAP_PX = 3;
const TOTAL_HOURS = 24;
const HOUR_COL_WIDTH = '3.75rem';
// On mobile the column needs to be slightly wider so "00:00" fits on one line without wrapping
const MOBILE_HOUR_COL_WIDTH = '4.5rem';
const MAX_CONTAINER_HEIGHT = '37.5rem';

type Props = {
  sessions: SessionLite[];
  onViewSession: (session: SessionLite) => void;
  onOpenSessionModal?: (session: SessionLite) => void;
  onSchedule?: () => void;
  canSchedule?: boolean;
  dayISO?: string;
};

// Extract hour and minute from a date string, treating "YYYY-MM-DD HH:MM:SS"
// as BA local time (the format returned by the by-day API).
// For ISO T-format strings, convert via Intl to BA timezone.
function getBAPartsFromDateStr(dateStr: string, timeZone: string = BA_TIMEZONE): { hour: number; minute: number } {
  if (!dateStr) return { hour: 0, minute: 0 };
  if (!dateStr.includes('T')) {
    // Space-separated "YYYY-MM-DD HH:MM:SS" — time is already in BA timezone
    const space = dateStr.indexOf(' ');
    if (space !== -1) {
      const [h, m] = dateStr.slice(space + 1).split(':').map(Number);
      if (!isNaN(h) && !isNaN(m)) return { hour: h, minute: m };
    }
  }
  // ISO with timezone indicator — convert to the therapist's timezone via Intl
  const d = new Date(dateStr);
  // A missing/unparseable dateStr must not crash Intl.DateTimeFormat — fall back
  // to midnight rather than propagating "Invalid time value" up through render.
  if (Number.isNaN(d.getTime())) return { hour: 0, minute: 0 };
  const df = new Intl.DateTimeFormat('en-CA', {
    timeZone, hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const parts = df.formatToParts(d);
  const h = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10);
  const m = parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0', 10);
  return { hour: h === 24 ? 0 : h, minute: m };
}

// BA-local calendar day (YYYY-MM-DD) for a session date string — used to scope the
// "Sesiones Canceladas" list to the selected day even if `sessions` ever carried extras.
function getBADateISO(dateStr: string): string {
  if (!dateStr.includes('T')) {
    const space = dateStr.indexOf(' ');
    if (space !== -1) return dateStr.slice(0, space);
  }
  const d = new Date(dateStr);
  return new Intl.DateTimeFormat('en-CA', { timeZone: BA_TIMEZONE }).format(d);
}

// Current BA time from a timestamp — same approach for the time indicator
function getNowBAParts(nowMs: number, timeZone: string = BA_TIMEZONE): { hour: number; minute: number } {
  const d = new Date(nowMs);
  const df = new Intl.DateTimeFormat('en-CA', {
    timeZone, hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const parts = df.formatToParts(d);
  const h = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10);
  const m = parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0', 10);
  return { hour: h === 24 ? 0 : h, minute: m };
}

// Convert BA hour+minute to rem from top of grid
function topRem(hour: number, minute: number, rowH: number): number {
  return (hour + minute / 60) * rowH;
}

// Returns "GMT-3", "GMT+5", etc. from an IANA timezone string
function getGmtLabel(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    const tzName = parts.find((p) => p.type === 'timeZoneName')?.value ?? 'GMT';
    return tzName.replace(/:00$/, '');
  } catch {
    return 'GMT';
  }
}

type PatientTimeInfo = { timeRange: string; city: string; iso2: string | null };

// Computes patient local start/end time for the tooltip.
// Returns null if patientTimezone is absent or computation fails.
function computePatientTimeInfo(session: SessionLite, timeZone: string = BA_TIMEZONE): PatientTimeInfo | null {
  const tz = session.patientTimezone;
  if (!tz) return null;
  try {
    const { hour, minute } = getBAPartsFromDateStr(session.date, timeZone);
    const dateStr = session.dateISO || session.date.split(' ')[0] || session.date.split('T')[0];
    const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    const utcStart = localTzToUTC(dateStr, timeStr, timeZone);
    const matchedType = SESSION_APPOINTMENT_TYPES.find((t) => t.name === session.therapyType);
    const duration = matchedType?.duration ?? 60;
    const utcEnd = new Date(utcStart.getTime() + duration * 60000);
    const patientStart = isoToZoneParts(utcStart.toISOString(), tz).time;
    const patientEnd = isoToZoneParts(utcEnd.toISOString(), tz).time;
    const iso2 = timezoneToCountryCode(tz);
    const city = tz.split('/').pop()?.replace(/_/g, ' ') ?? '';
    return { timeRange: `${patientStart} – ${patientEnd}`, city, iso2 };
  } catch {
    return null;
  }
}

// Same joinable-session logic as SessionsTimeline
function computeJoinableIds(sessions: SessionLite[]): Set<string> {
  const now = Date.now();
  const candidates: { id: string; startMs: number; inProgress: boolean }[] = [];
  for (const s of sessions) {
    if (s.isCancelled) continue;
    const startMs = parseSessionDate(s.date).getTime();
    const matchedType = SESSION_APPOINTMENT_TYPES.find((t) => t.name === s.therapyType);
    const duration = matchedType?.duration ?? 60;
    const endMs = startMs + duration * 60000;
    if (startMs <= now + 6 * 3600000 && now < endMs) {
      candidates.push({ id: s.id, startMs, inProgress: now >= startMs });
    }
  }
  const ids = new Set<string>();
  const inProgress = candidates.find((c) => c.inProgress);
  if (inProgress) ids.add(inProgress.id);
  const upcoming = candidates
    .filter((c) => !c.inProgress)
    .sort((a, b) => a.startMs - b.startMs)[0];
  if (upcoming) ids.add(upcoming.id);
  return ids;
}

// Tooltip component — rendered at document.body via portal
const TOOLTIP_ESTIMATED_HEIGHT = 80;
const PatientTooltip: React.FC<{ rect: DOMRect; info: PatientTimeInfo }> = ({ rect, info }) => {
  const showBelow = rect.top < TOOLTIP_ESTIMATED_HEIGHT + 16;
  const top = showBelow ? rect.bottom + 8 : rect.top - 8;
  const left = rect.left + rect.width / 2;
  const arrowStyle: React.CSSProperties = showBelow
    ? {
        position: 'absolute', top: -6, left: '50%', transform: 'translateX(-50%)',
        width: 0, height: 0,
        borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
        borderBottom: '6px solid #1e3a5f',
      }
    : {
        position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%)',
        width: 0, height: 0,
        borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
        borderTop: '6px solid #1e3a5f',
      };
  return (
    <div
      data-testid="day-timeline-patient-tooltip"
      style={{
        position: 'fixed',
        left,
        top,
        transform: showBelow ? 'translateX(-50%)' : 'translate(-50%, -100%)',
        background: '#1e3a5f',
        color: '#fff',
        borderRadius: '8px',
        padding: '8px 12px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
        zIndex: 9999,
        pointerEvents: 'none',
        minWidth: '160px',
        maxWidth: '220px',
      }}
    >
      <div style={{ position: 'relative' }}>
        <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', margin: 0, marginBottom: '3px' }}>
          Horario Paciente
        </p>
        <p
          data-testid="day-timeline-tooltip-time"
          style={{ fontSize: '13px', fontWeight: 500, margin: 0, marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          <User size={12} style={{ flexShrink: 0, opacity: 0.75 }} />
          <span>{info.timeRange}</span>
        </p>
        {(info.city || info.iso2) && (
          <p
            data-testid="day-timeline-tooltip-city"
            style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', margin: 0 }}
          >
            {info.city && `· ${info.city}`}{info.iso2 && <> <CountryFlag iso2={info.iso2} size="11px" /></>}
          </p>
        )}
        <div style={arrowStyle} />
      </div>
    </div>
  );
};

export const DayTimelineGrid: React.FC<Props> = ({
  sessions,
  onViewSession,
  onOpenSessionModal,
  onSchedule,
  canSchedule = true,
  dayISO,
}) => {
  const { size } = useWidth();
  const isMobile = size === 's';
  const { psychologist } = usePsychologistContext();
  const therapistTz = psychologist?.timezone || BA_TIMEZONE;
  const rowH = isMobile ? ROW_HEIGHT_REM : DESKTOP_ROW_HEIGHT_REM;
  const gmtLabel = React.useMemo(() => getGmtLabel(therapistTz), [therapistTz]);
  const countryCode = timezoneToCountryCode(therapistTz);
  const gridHeightRem = rowH * TOTAL_HOURS;
  const todayISO = toISODate(new Date());
  const isToday = !!dayISO && dayISO === todayISO;
  const isPastDay = !!dayISO && dayISO < todayISO;

  const [isCancelledOpen, setIsCancelledOpen] = React.useState(false);
  const [nowMs, setNowMs] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (!isToday) return;
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [isToday]);

  const scrollRef = useRef<HTMLDivElement>(null);

  const [tooltipState, setTooltipState] = React.useState<{
    id: string;
    rect: DOMRect;
    info: PatientTimeInfo;
  } | null>(null);
  const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
  }, []);

  const joinableIds = React.useMemo(() => computeJoinableIds(sessions), [sessions]);

  const flatSorted = React.useMemo(
    () =>
      sessions
        .filter((s) => !s.isCancelled)
        .slice()
        .sort((a, b) => parseSessionDate(a.date).getTime() - parseSessionDate(b.date).getTime()),
    [sessions],
  );

  // Sesiones canceladas del día seleccionado únicamente. `sessions` ya debería venir
  // filtrado por día desde el hook, pero se revalida contra `dayISO` para blindarse
  // ante cualquier fuga de sesiones canceladas de otros días.
  const cancelledForDay = React.useMemo(
    () =>
      sessions.filter((s) => s.isCancelled && (!dayISO || getBADateISO(s.date) === dayISO)),
    [sessions, dayISO],
  );

  // Auto-scroll: on mobile show first session/current time near top (1 row buffer above);
  // on desktop center in viewport.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const remToPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const effectiveRowH = isMobile ? ROW_HEIGHT_REM : DESKTOP_ROW_HEIGHT_REM;
    const rowPx = effectiveRowH * remToPx;
    let targetPx: number;
    if (isToday) {
      const { hour, minute } = getNowBAParts(nowMs, therapistTz);
      targetPx = topRem(hour, minute, effectiveRowH) * remToPx;
    } else if (flatSorted.length > 0) {
      const { hour, minute } = getBAPartsFromDateStr(flatSorted[0].date, therapistTz);
      targetPx = topRem(hour, minute, effectiveRowH) * remToPx;
    } else {
      targetPx = topRem(8, 0, effectiveRowH) * remToPx;
    }
    const containerH = el.clientHeight;
    if (isMobile) {
      el.scrollTop = Math.max(0, targetPx - rowPx);
    } else {
      el.scrollTop = Math.max(0, targetPx - containerH / 2);
    }
  }, [dayISO]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!sessions || sessions.length === 0) {
    return (
      <EmptyState
        title="Este día no tienes sesiones agendadas"
        description="Agenda tus sesiones con anticipación para seguir acompañando a nuestros pacientes."
        actionLabel="Agendar sesión"
        onAction={onSchedule}
        showAction={canSchedule && !isPastDay}
        actionButtonId="sessions-empty-schedule-btn"
      />
    );
  }

  const nowBAParts = getNowBAParts(nowMs, therapistTz);
  const nowTopRemVal = topRem(nowBAParts.hour, nowBAParts.minute, rowH);

  return (
    <div className="text-left" data-testid="day-timeline-grid">
      {/* Scrollable 24h grid */}
      <div
        ref={scrollRef}
        style={{
          overflowY: 'auto',
          maxHeight: isMobile ? 'calc(100dvh - 180px)' : MAX_CONTAINER_HEIGHT,
          position: 'relative',
          paddingBottom: isMobile ? '5rem' : undefined,
        }}
        data-testid="day-timeline-scroll"
      >
        {/* Sticky GMT header — sticks to top of scroll container (Ajuste 2 & 3) */}
        <div
          data-testid="day-timeline-gmt-header"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 15,
            display: 'flex',
            background: '#fff',
            paddingBottom: '2px',
          }}
        >
          <div
            style={{
              width: isMobile ? MOBILE_HOUR_COL_WIDTH : HOUR_COL_WIDTH,
              flexShrink: 0,
              paddingRight: '0.5rem',
              color: 'var(--textos-gris, #6b7280)',
              fontSize: '11px',
              fontWeight: 500,
              userSelect: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '3px',
              whiteSpace: 'nowrap',
            }}
          >
            {countryCode && <span aria-hidden="true" data-testid="day-timeline-gmt-flag"><CountryFlag iso2={countryCode} size="0.7rem" /></span>}
            <span data-testid="day-timeline-gmt-label">{gmtLabel}</span>
          </div>
          <div style={{ flex: 1, borderLeft: '1px solid #EEF0F4' }} />
        </div>

        {/* Grid body: 24 rows + absolutely positioned cards */}
        <div style={{ position: 'relative', height: `${gridHeightRem + 4}rem` }}>

          {/* Hour rows — background grid lines + time labels */}
          {Array.from({ length: TOTAL_HOURS }, (_, i) => (
            <div
              key={i}
              data-testid={`day-timeline-row-${String(i).padStart(2, '0')}`}
              style={{
                position: 'absolute',
                top: `${i * rowH}rem`,
                left: 0,
                right: 0,
                height: `${rowH}rem`,
                display: 'flex',
                borderBottom: '1px solid #EEF0F4',
              }}
            >
              {/* Time label */}
              <div
                style={{
                  width: isMobile ? MOBILE_HOUR_COL_WIDTH : HOUR_COL_WIDTH,
                  flexShrink: 0,
                  paddingRight: '0.5rem',
                  paddingTop: '2px',
                  textAlign: 'right',
                  color: '#6b7280',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {`${i.toString().padStart(2, '0')}:00`}
              </div>
              {/* Vertical separator + empty grid cell */}
              <div style={{ flex: 1, borderLeft: '1px solid #EEF0F4' }} />
            </div>
          ))}

          {/* Session cards — absolutely positioned by BA start time */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: isMobile ? MOBILE_HOUR_COL_WIDTH : HOUR_COL_WIDTH,
              right: 0,
              bottom: 0,
              paddingLeft: '0.5rem',
            }}
          >
            {flatSorted.map((s) => {
              const { hour, minute } = getBAPartsFromDateStr(s.date, therapistTz);
              const top = topRem(hour, minute, rowH);
              const startMs = parseSessionDate(s.date).getTime();
              const isPast = !isNaN(startMs) && startMs < nowMs;
              const matchedType = SESSION_APPOINTMENT_TYPES.find((t) => t.name === s.therapyType);
              const duration = matchedType?.duration ?? 60;

              if (isMobile) {
                const blockHeightRem = (duration / 60) * ROW_HEIGHT_REM;
                const patientInfo = computePatientTimeInfo(s, therapistTz);
                return (
                  <div
                    key={s.id}
                    data-testid={`day-timeline-card-${s.id}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
                      setTooltipState(null);
                      (onOpenSessionModal ?? onViewSession)(s);
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && (onOpenSessionModal ?? onViewSession)(s)}
                    onMouseEnter={(e) => {
                      if (!patientInfo) return;
                      if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
                      const rect = e.currentTarget.getBoundingClientRect();
                      tooltipTimerRef.current = setTimeout(() => setTooltipState({ id: s.id, rect, info: patientInfo }), 175);
                    }}
                    onMouseLeave={() => {
                      if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
                      setTooltipState(null);
                    }}
                    style={{
                      position: 'absolute',
                      top: `${top}rem`,
                      left: '4px',
                      right: '4px',
                      height: `calc(${blockHeightRem}rem - ${MOBILE_BLOCK_GAP_PX}px)`,
                      background: 'var(--lila-lighter, #f3efff)',
                      borderLeft: '3px solid var(--lila, #6D57BC)',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      paddingLeft: '7px',
                      paddingRight: '4px',
                      paddingTop: '3px',
                      cursor: 'pointer',
                      zIndex: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      boxSizing: 'border-box',
                    }}
                  >
                    <span
                      data-testid={`day-block-name-${s.id}`}
                      style={{
                        fontWeight: 700,
                        fontSize: '13px',
                        color: 'var(--textos-azul, #1e3a5f)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: '1.25',
                        flexShrink: 0,
                      }}
                    >
                      {s.patientName}
                    </span>
                  </div>
                );
              }

              // Desktop — full SessionRow card (entire surface is clickeable)
              const handler = onOpenSessionModal ?? onViewSession;
              const patientInfo = computePatientTimeInfo(s, therapistTz);
              return (
                <div
                  key={s.id}
                  data-testid={`day-timeline-card-${s.id}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
                    setTooltipState(null);
                    handler(s);
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handler(s)}
                  onMouseEnter={(e) => {
                    if (!patientInfo) return;
                    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
                    const rect = e.currentTarget.getBoundingClientRect();
                    tooltipTimerRef.current = setTimeout(() => setTooltipState({ id: s.id, rect, info: patientInfo }), 175);
                  }}
                  onMouseLeave={() => {
                    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
                    setTooltipState(null);
                  }}
                  className="day-view-card-wrapper"
                  style={{
                    position: 'absolute',
                    top: `${top}rem`,
                    left: 0,
                    right: '0.5rem',
                    height: `calc(${rowH}rem - ${CARD_GAP_PX}px)`,
                    overflow: 'hidden',
                    paddingRight: '0.25rem',
                    cursor: 'pointer',
                    borderRadius: '14px',
                    transition: 'box-shadow 0.15s ease',
                  }}
                >
                  <SessionRow
                    session={{ ...s, timeLabel: '' }}
                    onViewSession={handler}
                    isPast={isPast}
                    isNextJoinable={joinableIds.has(s.id)}
                    hidePrimaryAction
                  />
                </div>
              );
            })}
          </div>

          {/* Current time indicator — only when viewing today */}
          {isToday && (
            <div
              data-testid="day-timeline-now-indicator"
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: `${nowTopRemVal}rem`,
                left: isMobile ? MOBILE_HOUR_COL_WIDTH : HOUR_COL_WIDTH,
                right: 0,
                height: 0,
                borderTop: '2px solid var(--lila-dark, #665090)',
                zIndex: 10,
                pointerEvents: 'none',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: -4,
                  top: -4,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: 'var(--lila-dark, #665090)',
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Cancelled sessions accordion — scoped to the selected day, rendered below the grid.
          marginRight clears the floating chat button (FloatingCxButton), which otherwise
          overlaps the chevron on the right edge of the toggle. */}
      {cancelledForDay.length > 0 && (
        <div
          className="sess-slot mt-3"
          style={{ marginRight: isMobile ? '4.5rem' : '5rem' }}
        >
          <button
            type="button"
            id="sess-tour-cancelled"
            data-testid="cancelled-section-toggle"
            aria-expanded={isCancelledOpen}
            onClick={() => setIsCancelledOpen((prev) => !prev)}
            className="flex items-center justify-between w-full px-4 py-2.5 pr-16 rounded-xl bg-red-50 border border-red-200 text-sm font-medium text-gray-700 hover:bg-red-100 transition-colors"
          >
            <span>Sesiones Canceladas ({cancelledForDay.length})</span>
            <ChevronDown
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isCancelledOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {isCancelledOpen && (
            <div className="mt-2 space-y-2">
              {cancelledForDay.map((s) => (
                <div
                  key={s.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => (onOpenSessionModal ?? onViewSession)(s)}
                  onKeyDown={(e) => e.key === 'Enter' && (onOpenSessionModal ?? onViewSession)(s)}
                  className="day-view-card-wrapper"
                  style={{ cursor: 'pointer', borderRadius: '14px', transition: 'box-shadow 0.15s ease' }}
                >
                  <SessionRow
                    session={{ ...s, timeLabel: '' }}
                    onViewSession={onOpenSessionModal ?? onViewSession}
                    hidePrimaryAction
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Patient timezone tooltip — rendered via portal so it's never clipped */}
      {tooltipState && createPortal(
        <PatientTooltip
          rect={tooltipState.rect}
          info={tooltipState.info}
        />,
        document.body,
      )}
    </div>
  );
};
